import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { useRelationshipsMap } from 'shared/hooks/use-relationships';
import { ID, IDPrefixes } from 'shared/types/id';
import { untaggedId } from 'shared/types/orderable-section';
import { Relationship } from 'shared/types/relationship';
import { SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

import { filterEmptyChildrenCascading } from './use-life-area-tree';

export type Options = {
  tasks: Task[];
  tags: Tag[];
  relationships: Relationship[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
};

export const useTagTree = ({
  tasks,
  tags,
  relationships,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
}: Options) => {
  const { t } = useTranslation();
  const relationshipsMap = useRelationshipsMap(relationships);

  return useMemo(() => {
    // Create tag tree items
    const tagTreeItems = tags.reduce<Record<ID, TreeItem>>((acc, tag) => {
      acc[tag.id] = {
        id: tag.id,
        type: TreeItemTypes.Tag,
        name: tag.name,
        children: [],
        isFolder: true,
      };
      return acc;
    }, {});

    // Add "Untagged" section
    tagTreeItems[untaggedId] = {
      id: untaggedId,
      type: TreeItemTypes.Tag,
      name: t('general.sections.untagged.title'),
      children: [],
      isFolder: true,
    };

    const tagIds = Object.keys(tagTreeItems);

    // Build custom task order for custom sorting mode
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? tagIds.reduce<ID[]>((acc, tagId) => {
            const tagOrder = customOrder?.[tagId] ?? [];
            acc.push(...tagOrder);
            return acc;
          }, [])
        : [];

    // Get all tasks with their tag associations
    const tasksWithTags = tasks.map((task) => {
      const taskPrefixedId = createPrefixId(task.id, IDPrefixes.Task);
      const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
      const tagIds = relatedIds
        .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
        .map((id) => getPrefixedId(id));
      return { task, tagIds };
    });

    // Sort tasks
    const sortedTasks = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    // Build tree items: add tasks to their tag sections
    const allTreeItems = sortedTasks.reduce<Record<ID, TreeItem>>(
      (acc, task) => {
        const taskWithTags = tasksWithTags.find(
          ({ task: t }) => t.id === task.id,
        );
        const taskTagIds = taskWithTags?.tagIds ?? [];

        // If task has no tags, add to "Untagged" section
        if (!taskTagIds.length) {
          const untaggedTreeItem = acc[untaggedId];
          if (untaggedTreeItem) {
            untaggedTreeItem.children?.push(task.id);
          }
        }

        // If task has tags, add to each tag section
        if (taskTagIds.length) {
          taskTagIds.forEach((tagId) => {
            const tagTreeItem = acc[tagId];
            if (tagTreeItem) {
              tagTreeItem.children?.push(task.id);
            }
          });
        }

        // Add task tree item
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };

        return acc;
      },
      tagTreeItems,
    );

    // Add New Task row to each Tag section
    if (!hideNewTaskRow) {
      Object.keys(tagTreeItems).forEach((tagId) => {
        const tagTreeItem = allTreeItems[tagId];
        if (tagTreeItem) {
          if (!tagTreeItem.children?.length && !showEmptyGroups) {
            return;
          }

          const newTaskItemId = `${tagId}-new-task`;
          tagTreeItem.children?.push(newTaskItemId);
          allTreeItems[newTaskItemId] = {
            id: newTaskItemId,
            name: 'New Task',
            type: TreeItemTypes.NewTask,
          };
        }
      });
    }

    // Filter empty sections
    const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
    const topLevelTagIds = Object.keys(filteredTreeItems).filter(
      (id) => filteredTreeItems[id].type === TreeItemTypes.Tag,
    );

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: topLevelTagIds,
      },
      ...filteredTreeItems,
    };
  }, [
    customOrder,
    relationshipsMap,
    sortingMode,
    tags,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
    hideNewTaskRow,
    t,
  ]);
};
